// ============================================================================
// JARVISTRAVEL - DESTINATION IMAGERY
// Decides what to ask for. The provider crawl (GetYourGuide, Viator,
// Withlocals) deliberately takes no images, so an Explore card arrives with a
// name and a destination and nothing else. That is enough:
//
//   "Colosseum Underground Tour" in Rome  -> the Colosseum photo   (its title)
//   "Vespa Tour of Rome" in Rome          -> the Rome photo        (its city)
//   a city we have nothing for            -> ''  -> brand gradient
//
// Queries are tried most specific first and the first photo wins. Whether a
// title actually names a landmark is not something this file can know, and it
// does not need to: it asks, and the server rejects a result that is not about
// what was asked. Verified against the live API — "Colosseum Underground Tour"
// and "Vatican Museums & Sistine Chapel" resolve on their titles, while "Vespa
// Tour of Rome" and "Pasta Making Class in Trastevere" find nothing and fall
// through to the city, which is the intended behaviour and not a gap.
//
// Scoring the results is the server's job: it holds the access key, the shared
// request budget, and the cache, none of which belong in a browser bundle.
// ============================================================================

import type { ActivityType, ItineraryDay } from '@/app/types';

export interface ImageQuery {
  /** Experience name, e.g. "Colosseum Underground Tour". */
  title?: string;
  /** "Rome, Italy" or "Rome". */
  destination?: string;
  /** Category slug, e.g. "cultural". A weak signal, tried after the destination
   *  and before any named places (photoQueries orders title, destination,
   *  category, then places). */
  category?: string;
  /** Named places on the trip, e.g. ["Disneyland Park"]. Tried after the
   *  destination, so a city whose own name is in no photo's description still
   *  finds a hero of a place on the trip: "Anaheim, CA" matches nothing, but
   *  "Disneyland Park" does. The server's relevance rule still rejects a result
   *  that is not about the place asked for, so these never put an unrelated
   *  photo on a trip (JAR-2440). */
  places?: string[];
}

/**
 * Lowercase, unaccented, punctuation flattened to single spaces, so
 * "Sagrada Família" and "sagrada familia" are one query. The server normalizes
 * too; doing it here as well means two spellings of one place dedupe in this
 * session before either becomes a request.
 */
export function normalizeQuery(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/**
 * The queries to try, most specific first, deduplicated with empties dropped.
 * An empty list means there is nothing worth asking about, and the caller
 * shows the gradient without making a request.
 */
export function photoQueries(query: ImageQuery): string[] {
  const ordered = [query.title, query.destination, query.category, ...(query.places ?? [])]
    .map((part) => normalizeQuery(part ?? ''))
    .filter(Boolean);
  return [...new Set(ordered)];
}

// The activity types worth a hero photo: the sights, not the meals, transfers
// or hotel check-ins. A trip's "Disneyland Park" is a photo; its "Welcome
// dinner" is not, and asking for one risks a stock-food result the destination
// never wanted.
const HERO_PLACE_TYPES = new Set<ActivityType>(['attraction', 'tour']);
// A hero needs one photo, and resolveFirst stops at the first hit, so this only
// caps how many MISSING-photo queries a gradient trip can fan out to. Six named
// sights is plenty to find one that resolves without turning a blank trip into a
// burst of lookups.
const MAX_HERO_PLACES = 6;

// photoPlacesFromItinerary pulls the named sights out of a trip's itinerary, in
// order, deduped and capped, to widen the hero's query past the destination
// alone (JAR-2440). The hero asked for the destination only — "Anaheim, CA",
// which no Unsplash description says — while the Explore cards already asked for
// their activity names and found photos; this gives the hero the same reach.
export function photoPlacesFromItinerary(itinerary: Pick<ItineraryDay, 'activities'>[] | undefined): string[] {
  const names: string[] = [];
  for (const day of itinerary ?? []) {
    for (const activity of day.activities ?? []) {
      if (HERO_PLACE_TYPES.has(activity.type) && activity.name.trim()) {
        names.push(activity.name.trim());
      }
    }
  }
  return [...new Set(names)].slice(0, MAX_HERO_PLACES);
}

// tripPhotoQuery is the one question every hero of a trip asks: Home, the trip
// plan, the journal story and its cards. Only Home asked by name and sights; the
// others asked for the destination alone, so a destination with no photo left
// them on the gradient while Home kept one (JAR-3693). One question also shares
// one session answer, so a trip shows the same photo everywhere.
export function tripPhotoQuery(
  trip: { name?: string; destination?: string; itinerary?: Pick<ItineraryDay, 'activities'>[] } | null | undefined,
): ImageQuery {
  return {
    title: trip?.name,
    destination: trip?.destination,
    places: photoPlacesFromItinerary(trip?.itinerary),
  };
}

/** True for a URL served by Unsplash's image CDN. Exact host match — a
 *  substring test would accept `images.unsplash.com.evil.com`. */
export function isUnsplashImage(url?: string): boolean {
  if (!url) return false;
  try {
    return new URL(url).hostname === 'images.unsplash.com';
  } catch {
    return false;
  }
}
